import * as XLSX from 'xlsx';

/**
 * Exporta contratos y pagos del usuario a un archivo Excel
 * @param {Array} contracts - Lista de contratos del usuario
 * @param {Array} payments - Lista de pagos del usuario
 * @param {string} userName - Nombre del usuario
 */
export const exportToExcel = (contracts, payments, userName = 'Usuario') => {
  // Crear un nuevo libro de Excel
  const wb = XLSX.utils.book_new();

  // ========================================
  // HOJA 1: CONTRATOS
  // ========================================
  const contractsData = contracts.map(contract => ({
    'ID': contract.id,
    'Dirección': contract.propertyAddress,
    'Tipo': contract.propertyType,
    'Inquilino': contract.tenantName,
    'DNI Inquilino': contract.tenantDNI,
    'Teléfono': contract.tenantPhone,
    'Email': contract.tenantEmail,
    'Propietario': contract.ownerName,
    'DNI Propietario': contract.ownerDNI,
    'Fecha Inicio': contract.startDate,
    'Fecha Fin': contract.endDate,
    'Alquiler Base': `$${contract.baseRent?.toLocaleString('es-AR') || 0}`,
    'Índice': contract.indexType || 'N/A',
    'Frecuencia Aumento': `${contract.increaseFrequencyMonths || 12} meses`,
    'Estado': contract.status,
    'Última Actualización': contract.lastUpdate || 'N/A'
  }));

  const wsContracts = XLSX.utils.json_to_sheet(contractsData);

  // Configurar anchos de columna para contratos
  wsContracts['!cols'] = [
    { wch: 6 },   // ID
    { wch: 35 },  // Dirección
    { wch: 15 },  // Tipo
    { wch: 25 },  // Inquilino
    { wch: 12 },  // DNI Inquilino
    { wch: 15 },  // Teléfono
    { wch: 30 },  // Email
    { wch: 25 },  // Propietario
    { wch: 12 },  // DNI Propietario
    { wch: 12 },  // Fecha Inicio
    { wch: 12 },  // Fecha Fin
    { wch: 15 },  // Alquiler Base
    { wch: 10 },  // Índice
    { wch: 18 },  // Frecuencia Aumento
    { wch: 12 },  // Estado
    { wch: 18 }   // Última Actualización
  ];

  XLSX.utils.book_append_sheet(wb, wsContracts, 'Contratos');

  // ========================================
  // HOJA 2: PAGOS
  // ========================================
  const paymentsData = payments.map(payment => {
    const contract = contracts.find(c => c.id === payment.contractId);
    return {
      'ID': payment.id,
      'Contrato': contract?.propertyAddress || 'N/A',
      'Inquilino': contract?.tenantName || 'N/A',
      'Mes': payment.month,
      'Fecha Vencimiento': payment.dueDate,
      'Monto Base': `$${payment.baseAmount?.toLocaleString('es-AR') || 0}`,
      'Monto Ajustado': `$${payment.adjustedAmount?.toLocaleString('es-AR') || 0}`,
      'Fecha Pago': payment.paidDate || 'Pendiente',
      'Método Pago': payment.paymentMethod || 'N/A',
      'Estado': payment.status,
      'Notas': payment.notes || ''
    };
  });

  const wsPayments = XLSX.utils.json_to_sheet(paymentsData);

  // Configurar anchos de columna para pagos
  wsPayments['!cols'] = [
    { wch: 6 },   // ID
    { wch: 35 },  // Contrato
    { wch: 25 },  // Inquilino
    { wch: 12 },  // Mes
    { wch: 18 },  // Fecha Vencimiento
    { wch: 15 },  // Monto Base
    { wch: 15 },  // Monto Ajustado
    { wch: 15 },  // Fecha Pago
    { wch: 18 },  // Método Pago
    { wch: 12 },  // Estado
    { wch: 40 }   // Notas
  ];

  XLSX.utils.book_append_sheet(wb, wsPayments, 'Pagos');

  // ========================================
  // HOJA 3: RESUMEN
  // ========================================
  const activeContracts = contracts.filter(c => c.status === 'ACTIVO').length;
  const totalIncome = payments
    .filter(p => p.status === 'PAGADO')
    .reduce((sum, p) => sum + (p.adjustedAmount || p.baseAmount || 0), 0);
  const pendingPayments = payments.filter(p => p.status === 'PENDIENTE').length;
  const overduePayments = payments.filter(p => p.status === 'VENCIDO').length;

  const summaryData = [
    { 'Concepto': 'Total Contratos', 'Valor': contracts.length },
    { 'Concepto': 'Contratos Activos', 'Valor': activeContracts },
    { 'Concepto': 'Contratos Finalizados', 'Valor': contracts.filter(c => c.status === 'FINALIZADO').length },
    { 'Concepto': '', 'Valor': '' },
    { 'Concepto': 'Total Pagos', 'Valor': payments.length },
    { 'Concepto': 'Pagos Realizados', 'Valor': payments.filter(p => p.status === 'PAGADO').length },
    { 'Concepto': 'Pagos Pendientes', 'Valor': pendingPayments },
    { 'Concepto': 'Pagos Vencidos', 'Valor': overduePayments },
    { 'Concepto': '', 'Valor': '' },
    { 'Concepto': 'Ingresos Totales', 'Valor': `$${totalIncome.toLocaleString('es-AR')}` },
    { 'Concepto': 'Alquiler Mensual Promedio', 'Valor': `$${(contracts.reduce((sum, c) => sum + (c.baseRent || 0), 0) / Math.max(activeContracts, 1)).toLocaleString('es-AR')}` }
  ];

  const wsSummary = XLSX.utils.json_to_sheet(summaryData);
  wsSummary['!cols'] = [
    { wch: 30 },  // Concepto
    { wch: 20 }   // Valor
  ];

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumen');

  // ========================================
  // GENERAR Y DESCARGAR ARCHIVO
  // ========================================
  const fileName = `Rentia_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);

  return fileName;
};

/**
 * Exporta solo contratos a Excel
 * @param {Array} contracts - Lista de contratos del usuario
 * @param {string} userName - Nombre del usuario
 */
export const exportContractsToExcel = (contracts, userName = 'Usuario') => {
  const wb = XLSX.utils.book_new();

  const contractsData = contracts.map(contract => ({
    'ID': contract.id,
    'Dirección': contract.propertyAddress,
    'Tipo': contract.propertyType,
    'Inquilino': contract.tenantName,
    'DNI Inquilino': contract.tenantDNI,
    'Teléfono': contract.tenantPhone,
    'Email': contract.tenantEmail,
    'Propietario': contract.ownerName,
    'DNI Propietario': contract.ownerDNI,
    'Fecha Inicio': contract.startDate,
    'Fecha Fin': contract.endDate,
    'Alquiler Base': `$${contract.baseRent?.toLocaleString('es-AR') || 0}`,
    'Índice': contract.indexType || 'N/A',
    'Frecuencia Aumento': `${contract.increaseFrequencyMonths || 12} meses`,
    'Estado': contract.status
  }));

  const ws = XLSX.utils.json_to_sheet(contractsData);
  ws['!cols'] = [
    { wch: 6 }, { wch: 35 }, { wch: 15 }, { wch: 25 }, { wch: 12 },
    { wch: 15 }, { wch: 30 }, { wch: 25 }, { wch: 12 }, { wch: 12 },
    { wch: 12 }, { wch: 15 }, { wch: 10 }, { wch: 18 }, { wch: 12 }
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Contratos');

  const fileName = `Rentia_Contratos_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);

  return fileName;
};

/**
 * Exporta solo pagos a Excel
 * @param {Array} payments - Lista de pagos del usuario
 * @param {Array} contracts - Lista de contratos para obtener referencias
 * @param {string} userName - Nombre del usuario
 */
export const exportPaymentsToExcel = (payments, contracts, userName = 'Usuario') => {
  const wb = XLSX.utils.book_new();

  const paymentsData = payments.map(payment => {
    const contract = contracts.find(c => c.id === payment.contractId);
    return {
      'ID': payment.id,
      'Contrato': contract?.propertyAddress || 'N/A',
      'Inquilino': contract?.tenantName || 'N/A',
      'Mes': payment.month,
      'Fecha Vencimiento': payment.dueDate,
      'Monto Base': `$${payment.baseAmount?.toLocaleString('es-AR') || 0}`,
      'Monto Ajustado': `$${payment.adjustedAmount?.toLocaleString('es-AR') || 0}`,
      'Fecha Pago': payment.paidDate || 'Pendiente',
      'Método Pago': payment.paymentMethod || 'N/A',
      'Estado': payment.status,
      'Notas': payment.notes || ''
    };
  });

  const ws = XLSX.utils.json_to_sheet(paymentsData);
  ws['!cols'] = [
    { wch: 6 }, { wch: 35 }, { wch: 25 }, { wch: 12 }, { wch: 18 },
    { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 12 }, { wch: 40 }
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Pagos');

  const fileName = `Rentia_Pagos_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);

  return fileName;
};
